import React from 'react';
import { Link } from 'react-router-dom';
import './Privacy.css';

export default function Privacy() {
  return (
    <div className="legal-container">
      <div className="legal-glow" />
      
      <div className="legal-card">
        <div className="legal-header">
          <h1>Privacy Policy</h1>
          <div className="legal-meta">
            <span className="meta-item">Effective Date: January 1, 2026</span>
            <span className="meta-divider">•</span>
            <span className="meta-item">Last Updated: January 31, 2026</span>
          </div>
          <div className="legal-company">
            <span>APPCORE LABS LIMITED</span>
            <span className="company-location">London, United Kingdom</span>
          </div>
        </div>

        <div className="legal-content">
          <p className="intro">
            This Privacy Policy ("Policy") describes how APPCORE LABS LIMITED, a company registered in England and Wales ("Company", "we", "our", or "us"), collects, uses, discloses, and protects your personal information when you access or use our mobile applications distributed through the Apple App Store (collectively, the "Services"). By using our Services, you acknowledge that you have read and understood this Policy.
          </p>

          <section className="legal-section">
            <h2><span className="section-number">01</span>Information We Collect</h2>
            <div className="section-content">
              <h3>1.1 Information You Provide</h3>
              <p>We may collect personal information that you voluntarily provide when using our Services, including but not limited to: your name, email address, and any other information you choose to provide when contacting our support team or creating an account.</p>
              
              <h3>1.2 Automatically Collected Information</h3>
              <p>When you use our Services, we automatically collect certain technical information, including: device type and model, operating system version, unique device identifiers, IP address, app usage statistics, crash logs and diagnostic data, and general location information (country/region level).</p>
              
              <h3>1.3 Third-Party Analytics</h3>
              <p>We may use third-party analytics services to help us understand how users interact with our Services. These services may collect information about your use of our applications in accordance with their own privacy policies.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">02</span>Use of Information</h2>
            <div className="section-content">
              <p>We use the information we collect for the following purposes:</p>
              <h3>2.1 Service Delivery</h3>
              <p>To provide, maintain, and improve our Services; to process transactions and send related information; and to respond to your comments, questions, and support requests.</p>
              
              <h3>2.2 Analytics and Development</h3>
              <p>To analyze usage patterns and trends; to develop new features and functionality; and to monitor and improve the performance and stability of our applications.</p>
              
              <h3>2.3 Legal Compliance</h3>
              <p>To comply with applicable laws, regulations, and legal processes; to protect our rights, privacy, safety, or property; and to enforce our Terms of Use.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">03</span>Disclosure of Information</h2>
            <div className="section-content">
              <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
              
              <h3>3.1 Service Providers</h3>
              <p>With trusted third-party service providers who assist us in operating our Services, subject to confidentiality obligations and data processing agreements.</p>
              
              <h3>3.2 Legal Requirements</h3>
              <p>When required by law, court order, or governmental authority, or when we believe disclosure is necessary to protect our rights or the safety of others.</p>
              
              <h3>3.3 Business Transfers</h3>
              <p>In connection with any merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">04</span>Data Security</h2>
            <div className="section-content">
              <p>We implement appropriate technical and organizational measures designed to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include encryption, secure data storage, and access controls.</p>
              <p>However, no method of transmission over the Internet or electronic storage is completely secure. While we strive to protect your information, we cannot guarantee its absolute security.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">05</span>Data Retention</h2>
            <div className="section-content">
              <p>We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Policy, unless a longer retention period is required or permitted by law. When we no longer need your information, we will securely delete or anonymize it.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">06</span>Your Rights</h2>
            <div className="section-content">
              <p>Depending on your location and applicable law, you may have certain rights regarding your personal information, including:</p>
              <p><strong>Right of Access:</strong> You may request a copy of the personal information we hold about you.</p>
              <p><strong>Right to Rectification:</strong> You may request correction of inaccurate or incomplete information.</p>
              <p><strong>Right to Erasure:</strong> You may request deletion of your personal information in certain circumstances.</p>
              <p><strong>Right to Object:</strong> You may object to certain types of processing of your personal information.</p>
              <p><strong>Right to Portability:</strong> You may request transfer of your information to another service provider.</p>
              <p>To exercise any of these rights, please contact us at <a href="mailto:hello@appcorelab.co">hello@appcorelab.co</a>. We will respond to your request within the timeframe required by applicable law.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">07</span>International Data Transfers</h2>
            <div className="section-content">
              <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those of your country. We take appropriate safeguards to ensure your information remains protected in accordance with this Policy.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">08</span>Children's Privacy</h2>
            <div className="section-content">
              <p>Our Services are not directed to children under the age of 13 (or the applicable age of digital consent in your jurisdiction). We do not knowingly collect personal information from children. If we become aware that we have collected information from a child without parental consent, we will take steps to delete that information promptly.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">09</span>Changes to This Policy</h2>
            <div className="section-content">
              <p>We may update this Privacy Policy from time to time to reflect changes in our practices or applicable laws. We will notify you of any material changes by posting the updated Policy on our website and updating the "Last Updated" date. Your continued use of our Services after any changes constitutes acceptance of the revised Policy.</p>
            </div>
          </section>

          <section className="legal-section">
            <h2><span className="section-number">10</span>Contact Us</h2>
            <div className="section-content">
              <p>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
              <div className="contact-info">
                <p><strong>APPCORE LABS LIMITED</strong></p>
                <p>Unit 501, Leroy House</p>
                <p>London, N1 3FY</p>
                <p>United Kingdom</p>
                <p>Email: <a href="mailto:hello@appcorelab.co">hello@appcorelab.co</a></p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <Link to="/" className="back-btn">
        <span className="back-arrow">←</span>
        Back to Home
      </Link>
    </div>
  );
}